import React, { useCallback, useMemo } from "react";
import { Description as DescriptionIcon } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";

import { WorkflowExecutionLog } from "fond/api";
import Modal from "fond/widgets/Modal";

import LogViewer from "./LogViewer";

export interface LogViewerModalProps {
  open: boolean;
  onClose: () => void;
  logs: WorkflowExecutionLog[];
  title?: string;
}

const LogViewerModal: React.FC<LogViewerModalProps> = ({ open, onClose, logs, title = "Execution Logs" }) => {
  const handleDownloadLog = useCallback((log: WorkflowExecutionLog) => {
    const blob = new Blob([log.Content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = log.Filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const handleDownloadAll = useCallback(() => {
    logs.forEach(handleDownloadLog);
  }, [logs, handleDownloadLog]);

  const renderContent = useMemo(() => {
    if (logs.length === 0) {
      return (
        <Box p={2}>
          <Typography color="text.secondary">No logs available for this execution.</Typography>
        </Box>
      );
    }

    return <LogViewer logs={logs} onDownload={handleDownloadLog} maxHeight="100%" />;
  }, [logs, handleDownloadLog]);

  return (
    <Modal
      open={open}
      onClose={() => onClose()}
      header={title}
      headerIcon={<DescriptionIcon />}
      variant="primary"
      maxWidth={false}
      PaperProps={{ sx: { height: "80vh", width: "70vw !important", maxWidth: "70vw !important" } }}
      DialogContentProps={{ sx: { display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 } }}
      content={renderContent}
      actions={
        <>
          {logs.length > 1 && (
            <Button onClick={handleDownloadAll} color="primary">
              Download All
            </Button>
          )}
          <Button onClick={() => onClose()} variant="contained">
            Close
          </Button>
        </>
      }
    />
  );
};

export default LogViewerModal;
